import * as React from 'react';
type RawProgram = GetProgramPurchasePageByIdQuery['program'];
type Program = Pick<
  RawProgram,
  | 'tags'
  | 'duration'
  | 'daysPerWeek'
  | 'sessionDuration'
  | 'difficulty'
  | 'location'
>;

type Props = {
  program: Program;
  primaryColor?: string;
};

const TagsComponent: React.FC<{ tags: string[]; primaryColor?: string }> = ({
  tags,
  primaryColor,
}) => (
  <div className="flex items-center">
    <BoltCicleAlt
      className="[width: 32px] [min-width: 32px] lg:[min-width: 48px]"
      color={primaryColor}
    />
    <div className="ml-5 [max-width: 200px]">{tags.slice(0, 4).join(', ')}</div>
  </div>
);

const DurationComponent: React.FC<{
  program: Props['program'];
  primaryColor?: string;
  pluralize: (word: string, amount: number) => string;
}> = ({ program, primaryColor, pluralize }) => (
  <div className="flex items-center ">
    <CalendarCheckAlt
      className="[width: 32px] [min-width: 32px] lg:[min-width: 48px]"
      color={primaryColor}
    />
    <div className="ml-5 whitespace-pre">
      {[
        program.duration ? program.duration?.join(' ') + ' program' : null,
        program.daysPerWeek
          ? program.daysPerWeek +
            ` ${pluralize('day', program.daysPerWeek)}/week`
          : null,
        program.sessionDuration ? program.sessionDuration + ' mins' : null,
      ]
        .filter((x) => x)
        .join('\n')}
    </div>
  </div>
);

const DifficultyComponent: React.FC<{
  difficulty: string;
  primaryColor?: string;
}> = ({ difficulty, primaryColor }) => (
  <div className="flex items-center">
    <LineUpCircle
      className="[width: 32px] [min-width: 32px] lg:[min-width: 48px]"
      color={primaryColor}
    />
    <div className="ml-5">
      Level: <br />
      {difficulty}
    </div>
  </div>
);

const LocationComponent: React.FC<{
  location: string;
  primaryColor?: string;
}> = ({ location, primaryColor }) => (
  <div className="flex items-center">
    <LocationPin
      className="[width: 32px] [min-width: 32px] lg:[min-width: 48px]"
      color={primaryColor}
    />
    <div className="ml-5">
      Location: <br />
      {location}
    </div>
  </div>
);
import { GetProgramPurchasePageByIdQuery } from 'generated/graphql';

export function ProgramStats({ program, primaryColor }: Props) {
  const pluralize = (word: string, amount: number) =>
    amount === 1 ? word : word + 's';

  const theme = {
    primaryColor,
  };

  const tagsJSX = program.tags?.length ? (
    <TagsComponent tags={program.tags} primaryColor={theme.primaryColor} />
  ) : null;

  const durationJSX =
    program.duration && Number(program.duration[0]) ? (
      <DurationComponent
        program={program}
        primaryColor={theme.primaryColor}
        pluralize={pluralize}
      />
    ) : null;

  const difficultyJSX = program.difficulty ? (
    <DifficultyComponent
      difficulty={program.difficulty}
      primaryColor={theme.primaryColor}
    />
  ) : null;

  const locationJSX = program.location ? (
    <LocationComponent
      location={program.location}
      primaryColor={theme.primaryColor}
    />
  ) : null;

  return (
    <div style={{ color: 'black' }}>
      <div className="[font-size: 16px] [line-height: 120%]">
        <div className="hidden lg:grid grid-cols-4">
          {tagsJSX}
          {durationJSX}
          {difficultyJSX}
          {locationJSX}
        </div>

        <div className="lg:hidden">
          <div className="gap-4 grid grid-cols-2">
            {tagsJSX}
            {durationJSX}
          </div>

          <div className="gap-4 grid grid-cols-2">
            {difficultyJSX}
            {locationJSX}
          </div>
        </div>
      </div>
      <div className="[border-bottom: 1px solid #292526] opacity-10 hidden lg:block" />
    </div>
  );
}

// ICONS:

const BoltCicleAlt = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M24 0.375C10.9725 0.375 0.375 10.9725 0.375 24C0.375 37.0275 10.9725 47.625 24 47.625C37.0275 47.625 47.625 37.0275 47.625 24C47.625 10.9725 37.0275 0.375 24 0.375ZM24 45.375C12.2145 45.375 2.625 35.7855 2.625 24C2.625 12.2145 12.2145 2.625 24 2.625C35.7855 2.625 45.375 12.2145 45.375 24C45.375 35.7855 35.7855 45.375 24 45.375ZM33.6255 21.5003H27.8745V11.625C27.8745 11.1593 27.5888 10.7408 27.1523 10.5743C26.7135 10.4078 26.2252 10.527 25.9124 10.8712L13.5374 24.621C13.2404 24.9518 13.164 25.4242 13.3463 25.8315C13.5263 26.2387 13.929 26.4997 14.3745 26.4997H20.1255V36.375C20.1255 36.8408 20.4112 37.2592 20.8477 37.4257C20.9805 37.4752 21.1155 37.5 21.2505 37.5C21.5633 37.5 21.8693 37.3695 22.0876 37.1265L34.4626 23.3768C34.7596 23.046 34.836 22.5735 34.6537 22.1662C34.4715 21.7612 34.0688 21.5003 33.6255 21.5003ZM22.3755 33.4433V25.3747C22.3755 24.7537 21.8715 24.2497 21.2505 24.2497H16.9012L25.6245 14.5545V22.623C25.6245 23.244 26.1285 23.748 26.7495 23.748H31.0988L22.3755 33.4433Z"
      fill={props.color}
    />
  </svg>
);

const CalendarCheckAlt = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M21.75 40.875H8.25C4.35975 40.875 2.625 39.1403 2.625 35.25V16.125H40.875V21.75C40.875 22.371 41.379 22.875 42 22.875C42.621 22.875 43.125 22.371 43.125 21.75V11.625C43.125 6.4725 40.4025 3.75 35.25 3.75H31.875V1.5C31.875 0.879 31.371 0.375 30.75 0.375C30.129 0.375 29.625 0.879 29.625 1.5V3.75H13.875V1.5C13.875 0.879 13.371 0.375 12.75 0.375C12.129 0.375 11.625 0.879 11.625 1.5V3.75H8.25C3.0975 3.75 0.375 6.4725 0.375 11.625V35.25C0.375 40.4025 3.0975 43.125 8.25 43.125H21.75C22.371 43.125 22.875 42.621 22.875 42C22.875 41.379 22.371 40.875 21.75 40.875ZM8.25 6H11.625V8.25C11.625 8.871 12.129 9.375 12.75 9.375C13.371 9.375 13.875 8.871 13.875 8.25V6H29.625V8.25C29.625 8.871 30.129 9.375 30.75 9.375C31.371 9.375 31.875 8.871 31.875 8.25V6H35.25C39.1403 6 40.875 7.73475 40.875 11.625V13.875H2.625V11.625C2.625 7.73475 4.35975 6 8.25 6ZM14.4375 24C14.4375 24.9338 13.6858 25.6875 12.7588 25.6875C11.834 25.6875 11.0625 24.9338 11.0625 24C11.0625 23.0662 11.7983 22.3125 12.7253 22.3125H12.7588C13.6858 22.3125 14.4375 23.0662 14.4375 24ZM23.4375 24C23.4375 24.9338 22.6858 25.6875 21.7588 25.6875C20.834 25.6875 20.0625 24.9338 20.0625 24C20.0625 23.0662 20.7983 22.3125 21.7253 22.3125H21.7588C22.6858 22.3125 23.4375 23.0662 23.4375 24ZM37.5 27.375C31.9177 27.375 27.375 31.9177 27.375 37.5C27.375 43.0822 31.9177 47.625 37.5 47.625C43.0822 47.625 47.625 43.0822 47.625 37.5C47.625 31.9177 43.0822 27.375 37.5 27.375ZM37.5 45.375C33.1575 45.375 29.625 41.8425 29.625 37.5C29.625 33.1575 33.1575 29.625 37.5 29.625C41.8425 29.625 45.375 33.1575 45.375 37.5C45.375 41.8425 41.8425 45.375 37.5 45.375ZM41.109 34.8292C41.5478 35.268 41.5478 35.9813 41.109 36.42L37.3583 40.1708C37.1468 40.3823 36.861 40.4993 36.5618 40.4993C36.2625 40.4993 35.9768 40.38 35.7653 40.1708L33.891 38.2965C33.4522 37.8578 33.4522 37.1444 33.891 36.7057C34.3297 36.2669 35.0431 36.2669 35.4818 36.7057L36.5596 37.7856L39.5138 34.8314C39.957 34.3904 40.668 34.3905 41.109 34.8292ZM14.4375 33C14.4375 33.9338 13.6858 34.6875 12.7588 34.6875C11.834 34.6875 11.0625 33.9338 11.0625 33C11.0625 32.0662 11.7983 31.3125 12.7253 31.3125H12.7588C13.6858 31.3125 14.4375 32.0662 14.4375 33Z"
      fill={props.color}
    />
  </svg>
);

const LineUpCircle = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M36.2895 18.3863C36.3458 18.5235 36.375 18.6699 36.375 18.8161V25.2958C36.375 25.9168 35.871 26.4208 35.25 26.4208C34.629 26.4208 34.125 25.9168 34.125 25.2958V21.5316L28.0635 27.5931C26.8643 28.7923 24.9203 28.7901 23.7233 27.5931L20.4068 24.2769C20.0873 23.9574 19.5653 23.9596 19.248 24.2769L13.5465 29.9782C13.326 30.1987 13.038 30.3067 12.75 30.3067C12.462 30.3067 12.174 30.1965 11.9535 29.9782C11.5147 29.5395 11.5147 28.8261 11.9535 28.3874L17.655 22.686C18.852 21.489 20.796 21.489 21.9953 22.686L25.3118 26.0023C25.6223 26.3128 26.16 26.3105 26.4705 26.0023L32.532 19.9411H28.7677C28.1467 19.9411 27.6427 19.4371 27.6427 18.8161C27.6427 18.1951 28.1467 17.6911 28.7677 17.6911H35.25C35.3963 17.6911 35.5425 17.7202 35.6798 17.7787C35.9543 17.8912 36.1748 18.1095 36.2895 18.3863ZM47.625 24C47.625 37.0275 37.0275 47.625 24 47.625C10.9725 47.625 0.375 37.0275 0.375 24C0.375 10.9725 10.9725 0.375 24 0.375C37.0275 0.375 47.625 10.9725 47.625 24ZM45.375 24C45.375 12.2145 35.7855 2.625 24 2.625C12.2145 2.625 2.625 12.2145 2.625 24C2.625 35.7855 12.2145 45.375 24 45.375C35.7855 45.375 45.375 35.7855 45.375 24Z"
      fill={props.color}
    />
  </svg>
);

const LocationPin = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="40"
    height="44"
    viewBox="0 0 40 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M20 0.625C9.45425 0.625 0.875 9.20425 0.875 19.75C0.875 30.9618 11.2903 37.8422 18.182 42.3939L19.3745 43.186C19.5635 43.312 19.7817 43.375 19.9977 43.375C20.2137 43.375 20.432 43.312 20.621 43.186L21.8135 42.3939C28.7052 37.8422 39.1205 30.9618 39.1205 19.75C39.125 9.20425 30.5457 0.625 20 0.625ZM20.576 40.5175L20 40.8998L19.424 40.5175C12.9328 36.229 3.125 29.7513 3.125 19.75C3.125 10.444 10.694 2.875 20 2.875C29.306 2.875 36.875 10.444 36.875 19.75C36.875 29.7513 27.0672 36.2312 20.576 40.5175ZM20 13C16.2785 13 13.25 16.0285 13.25 19.75C13.25 23.4715 16.2785 26.5 20 26.5C23.7215 26.5 26.75 23.4715 26.75 19.75C26.75 16.0285 23.7215 13 20 13ZM20 24.25C17.5182 24.25 15.5 22.2318 15.5 19.75C15.5 17.2682 17.5182 15.25 20 15.25C22.4818 15.25 24.5 17.2682 24.5 19.75C24.5 22.2318 22.4818 24.25 20 24.25Z"
      fill={props.color}
    />
  </svg>
);
